import { SetStateAction, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/common/input/Input'
import { TextArea } from 'components/common/textArea/TextArea'
import { Button } from 'wiz-front-shared/components/button/Button'
import { SelectBox, SelectBoxItem, SelectBoxRef } from 'wiz-front-shared/components/selectBox/SelectBox'
import { Spacer } from 'wiz-front-shared/components/Spacer'
import { useQuery } from 'hooks/useQuery'
import { qnaStore } from 'store/qnaStore'
import { useAtomValue } from 'jotai'
import { QNA } from 'repository/types/QNA.types'
import { Header } from 'components/common/header/Header'
import { getServiceId, getLocale } from 'common/service'
import { closePopup } from 'common/webview'
import { BottomSheet } from 'wiz-front-shared/components/bottomSheet/BottomSheet'
import './QnaSend.css'

export const QNASend = () => {
    const { t } = useTranslation()
    const { navigate } = useQuery()
    const qnaCreateState = useAtomValue(qnaStore.qnaCreate.stateAtom)
    const inset = window.__DEVICE__?.inset ?? { navigationBarHeight: 0 }
    const serviceId = getServiceId()
    const locale = getLocale()

    // 서버(QnaSendRequest)의 @Size 제약과 동일하게 유지할 것
    const TITLE_MAX_LENGTH = 100
    const CONTENT_MAX_LENGTH = 10

    const [category, setCategory] = useState<SelectBoxItem | undefined>(undefined)
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [isSuccessSheetOpen, setIsSuccessSheetOpen] = useState(false)

    const selectBoxRef = useRef<SelectBoxRef>(null)
    const hasHandledSuccessRef = useRef(false)

    const categoryItems: SelectBoxItem[] = [
        { name: t('qna.category.BUG'), value: 'BUG' },
        { name: t('qna.category.ACCOUNT'), value: 'ACCOUNT' },
        { name: t('qna.category.PAYMENT'), value: 'PAYMENT' },
        { name: t('qna.category.ETC'), value: 'ETC' },
    ]

    const handleCategorySelect = (item: SelectBoxItem) => {
        setCategory(item)
        selectBoxRef.current?.close()
    }

    const handleSubmit = () => {
        if (!category || !title.trim() || !content.trim()) {
            return
        }

        // TODO: API call to submit QnA
        qnaStore.createQna({
            serviceId,
            categoryCode: String(category.value) as QNA.CategoryCode,
            title: title.trim(),
            content: content.trim(),
            isAnonymous: false,
            locale,
        })
    }

    useEffect(() => {
        if (qnaCreateState.isReady) {
            qnaStore.qnaCreate.onDefault()
            setIsSuccessSheetOpen(true)
        }
    }, [qnaCreateState.status])

    useEffect(() => {
        return () => {
            qnaStore.qnaCreate.onDefault()
        }
    }, [])

    const isFormValid = !!category && title.trim().length > 0 && content.trim().length > 0

    const handleSuccessClose = () => {
        if (hasHandledSuccessRef.current) return
        hasHandledSuccessRef.current = true
        setIsSuccessSheetOpen(false)

        window.setTimeout(() => {
            // iframe 진입 → 부모(DOKOCREW 등)에 종료 요청
            // 그 외(네이티브 WebView/독립 브라우저) → 목록 갱신 후 navigate(-1)
            if (window.parent !== window) {
                closePopup()
            } else {
                qnaStore.getQnaList({ serviceId, locale, pageNo: 1, pageSize: 20 })
                navigate(-1)
            }
        }, 300)
    }

    const handleSuccessSheetOpenChange = (value: SetStateAction<boolean>) => {
        const nextValue = typeof value === 'function' ? value(isSuccessSheetOpen) : value
        if (!nextValue && isSuccessSheetOpen) {
            handleSuccessClose()
            return
        }
        setIsSuccessSheetOpen(nextValue)
    }

    return (
        <div className="qna-send-wrapper">
            <Header title={t('qna.submit')} isBack />
            <Spacer height={30} />
            <div className="qna-send-form">
                <Input
                    inputVal={category?.name || ""}
                    title={t('qna.send.categoryLabel')}
                    isRequired
                    readOnly
                    placeholder={t('qna.send.categoryPlaceholder')}
                    onClick={() => selectBoxRef.current?.open()}
                    showClearButton={false}
                    notShowInfo
                />

                <SelectBox
                    ref={selectBoxRef}
                    items={categoryItems}
                    onSelect={handleCategorySelect}
                    selectedItem={category}
                />

                <Input
                    title={t('qna.send.titleLabel')}
                    isRequired
                    inputVal={title}
                    setInputVal={setTitle}
                    placeholder={t('qna.send.titlePlaceholder')}
                    maxLength={TITLE_MAX_LENGTH}
                    notShowInfo
                />

                <TextArea
                    title={t('qna.send.contentLabel')}
                    inputVal={content}
                    setInputVal={setContent}
                    placeholder={t('qna.send.contentPlaceholder')}
                    maxLength={CONTENT_MAX_LENGTH}
                    showCounter
                    notShowInfo
                    isRequired
                />
            </div>

            <div className="qna-send-submit-wrapper" style={{ paddingBottom: inset.navigationBarHeight }}>
                <p className="qna-send-notice">{t('qna.send.notice')}</p>
                {qnaCreateState.isError && <p className="qna-send-error">{t('qna.send.error')}</p>}
                <Spacer height={10} />
                <Button
                    text={qnaCreateState.isPending ? t('qna.send.submitting') : t('qna.send.submitButton')}
                    onPress={handleSubmit}
                    disabled={!isFormValid || qnaCreateState.isPending}
                />
                <Spacer height={10} />
            </div>

            <BottomSheet isOpen={isSuccessSheetOpen} setIsOpen={handleSuccessSheetOpenChange}>
                <div className="qna-send-success-sheet">
                    <h2 className="qna-send-success-title">{t('qna.send.successTitle')}</h2>
                    <p className="qna-send-success-message">{t('qna.send.successMessage')}</p>
                    <div className="qna-send-success-button-wrapper">
                        <Button text={t('qna.send.successConfirm')} onPress={handleSuccessClose} />
                    </div>
                </div>
            </BottomSheet>
        </div>
    )
}
